import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { AltYBmadArchitectureDecision, AltYBmadBuildState, AltYBmadBuildTask, AltYBmadDesignPage, AltYBmadQuestion, AltYBmadSource, AltYBmadTask, AltYBmadWorkflow } from '../types/global';
import { MarkdownText } from './MarkdownText';

type BmadWorkflowViewProps = {
  workflow: AltYBmadWorkflow;
  buildState?: AltYBmadBuildState | null;
  // Launch the dependency-aware BMAD build manager. Omitted -> hidden.
  onBuild?: () => void;
  onOpenChat?: (runId: number) => void;
  // Back to the OneSheet iframe (desktop) / the conversation (mobile overlay).
  // Omitted → no close button.
  onClose?: () => void;
  // True while a turn/build request is active — disables Build so a click can't
  // double fire and the user sees work is still running.
  busy?: boolean;
};

// A right-side tab. `available` decides whether it shows at all (a "bug" run
// only has rephrase + tasks, so the four-piece tabs stay hidden).
type Tab = {
  id: string;
  label: string;
  icon: string;
  available: boolean;
  render: () => ReactNode;
};

// Newline-or-array → clean string lines. The agent usually emits arrays, but be
// forgiving if a piece arrives as one multi-line string.
function asLines(value: string[] | string | undefined): string[] {
  if (!value) return [];
  const arr = Array.isArray(value) ? value : value.split(/\r?\n/);
  return arr.map((s) => String(s).trim()).filter(Boolean);
}

function ListPanel({ items, ordered }: { items: string[]; ordered?: boolean }) {
  if (ordered) {
    return <ol className="alty-bmad-list">{items.map((t, i) => <li key={i}>{t}</li>)}</ol>;
  }
  return <ul className="alty-bmad-list">{items.map((t, i) => <li key={i}>{t}</li>)}</ul>;
}

type NormalizedQuestion = {
  question: string;
  guess?: string;
  confidence?: string;
};

function formatConfidence(value: AltYBmadQuestion['confidence']): string | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) {
    const pct = value <= 1 ? Math.round(value * 100) : Math.round(value);
    return `${Math.max(0, Math.min(100, pct))}%`;
  }
  const text = String(value).trim();
  if (!text) return undefined;
  if (/^\d+(\.\d+)?$/.test(text)) {
    const pct = Number(text);
    if (Number.isFinite(pct)) return `${Math.max(0, Math.min(100, Math.round(pct)))}%`;
  }
  return text;
}

function normalizeQuestions(value: AltYBmadWorkflow['questions']): NormalizedQuestion[] {
  if (!value) return [];
  const arr = Array.isArray(value) ? value : value.split(/\r?\n/);
  return arr.map((item) => {
    if (typeof item === 'string') {
      const question = item.trim();
      return question ? { question } : null;
    }
    if (!item || typeof item !== 'object') return null;
    const question = String(item.question || '').trim();
    if (!question) return null;
    const guess = String(item.guess || item.guessedAnswer || item.answerGuess || '').trim();
    return {
      question,
      ...(guess ? { guess } : {}),
      ...(item.confidence != null ? { confidence: formatConfidence(item.confidence) } : {}),
    };
  }).filter((item): item is NormalizedQuestion => !!item);
}

function QuestionsPanel({ questions }: { questions: NormalizedQuestion[] }) {
  return (
    <ul className="alty-bmad-question-list">
      {questions.map((item, i) => (
        <li key={i} className="alty-bmad-question">
          <div className="alty-bmad-question-head">
            <span className="alty-bmad-question-text">{item.question}</span>
            {item.confidence ? <span className="alty-bmad-confidence">{item.confidence}</span> : null}
          </div>
          {item.guess ? (
            <p className="alty-bmad-question-guess">
              <span>Guess:</span> {item.guess}
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

type NormalizedArchitectureDecision = {
  question: string;
  answer?: string;
  confidence?: string;
};

function normalizeArchitectureDecisions(value: AltYBmadWorkflow['architectureDecisions']): NormalizedArchitectureDecision[] {
  if (!value) return [];
  const arr = Array.isArray(value) ? value : value.split(/\r?\n/);
  return arr.map((item) => {
    if (typeof item === 'string') {
      const question = item.trim();
      return question ? { question } : null;
    }
    if (!item || typeof item !== 'object') return null;
    const decision = item as AltYBmadArchitectureDecision;
    const question = String(decision.question || '').trim();
    if (!question) return null;
    const answer = String(
      decision.answer
      || decision.guess
      || decision.decision
      || decision.recommendation
      || '',
    ).trim();
    return {
      question,
      ...(answer ? { answer } : {}),
      ...(decision.confidence != null ? { confidence: formatConfidence(decision.confidence) } : {}),
    };
  }).filter((item): item is NormalizedArchitectureDecision => !!item);
}

function ArchitecturePanel({ decisions }: { decisions: NormalizedArchitectureDecision[] }) {
  return (
    <ul className="alty-bmad-question-list">
      {decisions.map((item, i) => (
        <li key={i} className="alty-bmad-question">
          <div className="alty-bmad-question-head">
            <span className="alty-bmad-question-text">{item.question}</span>
            {item.confidence ? <span className="alty-bmad-confidence">{item.confidence}</span> : null}
          </div>
          {item.answer ? (
            <p className="alty-bmad-question-guess">
              <span>Answer:</span> {item.answer}
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

const imageMarkdownRe = /^!\[([^\]]*)]\(([^)\s]+)(?:\s+"[^"]*")?\)$/;
const imageUrlRe = /(?:https?:\/\/|\/api\/alty\/generated\/)\S+?\.(?:png|jpe?g|gif|webp)(?:\?\S*)?/i;

type DesignImage = {
  url: string;
  alt: string;
  caption?: string;
};

function usableHref(value: string | undefined): string | undefined {
  const text = String(value || '').trim();
  if (!text || text === '#' || /^https?:\/\/[.…]+$/i.test(text)) return undefined;
  if (/^https?:\/\//i.test(text) || text.startsWith('/')) return text;
  return undefined;
}

type NormalizedSource = {
  title: string;
  type?: string;
  href?: string;
  path?: string;
  id?: string;
  description?: string;
  usedFor?: string;
  confidence?: string;
};

function sourceTypeLabel(value: string | undefined): string | undefined {
  const text = String(value || '').trim();
  if (!text) return undefined;
  return text
    .replace(/[_-]+/g, ' ')
    .replace(/\bidsys\b/i, 'IDsys')
    .replace(/\bclickup\b/i, 'ClickUp')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeSources(value: AltYBmadWorkflow['sources']): NormalizedSource[] {
  if (!value) return [];
  const arr = Array.isArray(value) ? value : value.split(/\r?\n/);
  return arr.map((item, index) => {
    if (typeof item === 'string') {
      const text = item.trim();
      if (!text) return null;
      return {
        title: text,
        ...(usableHref(text) ? { href: usableHref(text) } : {}),
      };
    }
    if (!item || typeof item !== 'object') return null;
    const source = item as AltYBmadSource;
    const href = usableHref(source.url || source.href);
    const path = String(source.path || '').trim();
    const id = source.id != null ? String(source.id).trim() : '';
    const title = String(
      source.name
      || source.title
      || source.label
      || source.url
      || source.href
      || source.path
      || (id ? `#${id}` : '')
      || `Source ${index + 1}`,
    ).trim();
    const description = String(source.description || source.summary || source.notes || '').trim();
    const usedFor = String(source.usedFor || source.usage || '').trim();
    return {
      title,
      ...(source.type ? { type: sourceTypeLabel(source.type) } : {}),
      ...(href ? { href } : {}),
      ...(path ? { path } : {}),
      ...(id ? { id } : {}),
      ...(description ? { description } : {}),
      ...(usedFor ? { usedFor } : {}),
      ...(source.confidence != null ? { confidence: formatConfidence(source.confidence) } : {}),
    };
  }).filter((item): item is NormalizedSource => !!item);
}

function SourcesPanel({ sources }: { sources: NormalizedSource[] }) {
  if (!sources.length) {
    return <p className="alty-bmad-text muted">No sources recorded yet.</p>;
  }
  return (
    <ul className="alty-bmad-source-list">
      {sources.map((source, i) => (
        <li key={`${source.title}-${i}`} className="alty-bmad-source">
          <div className="alty-bmad-source-head">
            <span className="alty-bmad-source-title">{source.title}</span>
            {source.confidence ? <span className="alty-bmad-confidence">{source.confidence}</span> : null}
          </div>
          <div className="alty-bmad-source-meta">
            {source.type ? <span className="alty-bmad-pill source">{source.type}</span> : null}
            {source.id ? <span>#{source.id}</span> : null}
            {source.path ? <code>{source.path}</code> : null}
            {source.href ? (
              <a href={source.href} target="_blank" rel="noopener noreferrer">
                open source <i className="fas fa-external-link-alt" aria-hidden="true" />
              </a>
            ) : null}
          </div>
          {source.description ? <div className="alty-bmad-text"><MarkdownText text={source.description} /></div> : null}
          {source.usedFor ? (
            <p className="alty-bmad-source-used">
              <span>Used for:</span> {source.usedFor}
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function designImageFrom(value: string | undefined, alt: string, caption?: string): DesignImage | null {
  const text = String(value || '').trim();
  if (!text) return null;
  const markdown = imageMarkdownRe.exec(text);
  if (markdown) {
    const url = usableHref(markdown[2]);
    return url ? { url, alt: markdown[1] || alt, caption } : null;
  }
  const direct = imageUrlRe.exec(text)?.[0] || text;
  const url = usableHref(direct);
  return url ? { url, alt, caption } : null;
}

function collectDesignImages(page: AltYBmadDesignPage): DesignImage[] {
  const items: DesignImage[] = [];
  const add = (image: DesignImage | null) => {
    if (!image || items.some((item) => item.url === image.url)) return;
    items.push(image);
  };
  add(designImageFrom(page.mockup, `Mockup of ${page.name || 'the page'}`, 'Mockup'));
  add(designImageFrom(page.mockupUrl, `Mockup of ${page.name || 'the page'}`, 'Mockup'));
  add(designImageFrom(page.mockupMarkdown, `Mockup of ${page.name || 'the page'}`, 'Mockup'));
  add(designImageFrom(page.image, `Design image for ${page.name || 'the page'}`));
  add(designImageFrom(page.imageUrl, `Design image for ${page.name || 'the page'}`));
  add(designImageFrom(page.screenshot, `Screenshot of ${page.name || 'the page'}`, 'Screenshot'));
  add(designImageFrom(page.screenshotUrl, `Screenshot of ${page.name || 'the page'}`, 'Screenshot'));
  if (Array.isArray(page.images)) {
    page.images.forEach((entry, index) => {
      if (typeof entry === 'string') {
        add(designImageFrom(entry, `Design image ${index + 1} for ${page.name || 'the page'}`));
      } else if (entry && typeof entry === 'object') {
        const raw = entry.url || entry.src || entry.href;
        add(designImageFrom(raw, entry.alt || `Design image ${index + 1} for ${page.name || 'the page'}`, entry.caption));
      }
    });
  }
  return items;
}

function StoryPanel({ items, designPages }: { items: string[]; designPages?: AltYBmadDesignPage[] }) {
  const pages = Array.isArray(designPages) ? designPages : [];
  return (
    <ol className="alty-bmad-story-list">
      {items.map((text, index) => {
        const page = pages[index];
        const images = page ? collectDesignImages(page) : [];
        return (
          <li key={index} className={images.length ? 'has-image' : ''}>
            <div className="alty-bmad-story-text">{text}</div>
            {page?.changes ? <div className="alty-bmad-text muted"><MarkdownText text={page.changes} /></div> : null}
            {images.length ? (
              <div className="alty-bmad-design-images">
                {images.map((image) => (
                  <a key={image.url} href={image.url} target="_blank" rel="noopener noreferrer" className="alty-bmad-mockup">
                    <img src={image.url} alt={image.alt} loading="lazy" />
                    {image.caption ? <span>{image.caption}</span> : null}
                  </a>
                ))}
              </div>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function designStepName(page: AltYBmadDesignPage | undefined, storyText: string | undefined, index: number): string {
  const explicit = String(page?.name || page?.url || '').trim();
  if (explicit) return explicit;
  const normalizedStory = String(storyText || '').replace(/^\s*\d+[\.)]\s*/, '').trim();
  if (normalizedStory) return normalizedStory;
  return `Step ${index + 1}`;
}

function renderDesignStep(page: AltYBmadDesignPage | undefined, storyText: string | undefined, index: number) {
  const pageHref = usableHref(page?.url);
  const images = page ? collectDesignImages(page) : [];
  return (
    <div className="alty-bmad-design-page">
      <div className="alty-bmad-design-head">
        <i className="fas fa-window-maximize" aria-hidden="true" />
        <span className="alty-bmad-design-name">{designStepName(page, storyText, index)}</span>
        {pageHref ? (
          <a href={pageHref} target="_blank" rel="noopener noreferrer" className="alty-bmad-design-link">
            open page <i className="fas fa-external-link-alt" aria-hidden="true" />
          </a>
        ) : null}
      </div>
      {storyText ? (
        <div className="alty-bmad-design-story">
          <span>Story step</span>
          <MarkdownText text={storyText} />
        </div>
      ) : null}
      {page?.changes ? <div className="alty-bmad-text"><MarkdownText text={page.changes} /></div> : null}
      {!page?.changes && !images.length ? (
        <p className="alty-bmad-text muted">No design details recorded for this step yet.</p>
      ) : null}
      {images.length ? (
        <div className="alty-bmad-design-images">
          {images.map((image) => (
            <a key={image.url} href={image.url} target="_blank" rel="noopener noreferrer" className="alty-bmad-mockup">
              <img src={image.url} alt={image.alt} loading="lazy" />
              {image.caption ? <span>{image.caption}</span> : null}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function DesignPanel({ design, storyItems }: {
  design: NonNullable<AltYBmadWorkflow['design']>;
  storyItems: string[];
}) {
  const pages: AltYBmadDesignPage[] = Array.isArray(design.pages) ? design.pages : [];
  const stepCount = Math.max(pages.length, storyItems.length);
  const [activeStep, setActiveStep] = useState(0);
  const activeDesignStep = stepCount > 0 ? Math.min(activeStep, stepCount - 1) : 0;

  useEffect(() => {
    if (stepCount > 0 && activeStep >= stepCount) {
      setActiveStep(stepCount - 1);
    }
  }, [activeStep, stepCount]);

  return (
    <div className="alty-bmad-design">
      {design.summary ? <div className="alty-bmad-text"><MarkdownText text={design.summary} /></div> : null}
      {stepCount > 1 ? (
        <nav className="alty-bmad-subtabs" role="tablist" aria-label="Design story steps">
          {Array.from({ length: stepCount }).map((_, index) => (
            <button
              key={index}
              type="button"
              role="tab"
              aria-selected={index === activeDesignStep}
              className={index === activeDesignStep ? 'alty-bmad-subtab active' : 'alty-bmad-subtab'}
              onClick={() => setActiveStep(index)}
              title={designStepName(pages[index], storyItems[index], index)}
            >
              <span className="alty-bmad-subtab-index">Step {index + 1}</span>
              <span className="alty-bmad-subtab-title">{designStepName(pages[index], storyItems[index], index)}</span>
            </button>
          ))}
        </nav>
      ) : null}
      {stepCount > 0 ? (
        <div role="tabpanel">
          {renderDesignStep(pages[activeDesignStep], storyItems[activeDesignStep], activeDesignStep)}
        </div>
      ) : null}
      {design.notes ? <div className="alty-bmad-text muted"><MarkdownText text={design.notes} /></div> : null}
    </div>
  );
}

function normalizeTaskTitle(title: string): string {
  return title.trim().replace(/\s+/g, ' ').toLowerCase();
}

function isEpicTask(task: AltYBmadTask): boolean {
  return /^epic\s*:/i.test(task.title.trim());
}

function displayEpicTitle(title: string): string {
  return title.replace(/^epic\s*:\s*/i, '').trim() || title;
}

function buildStatusLabel(status: string | undefined): string {
  switch (status) {
    case 'needs_review': return 'needs review';
    case 'running': return 'in progress';
    case 'complete': return 'complete';
    case 'failed': return 'failed';
    case 'blocked': return 'blocked';
    case 'queued': return 'queued';
    default: return status || '';
  }
}

function taskDependsOnEpic(dependsOn: string | undefined, epicTitles: Set<string>): boolean {
  if (!dependsOn) return false;
  const normalized = normalizeTaskTitle(dependsOn);
  return epicTitles.has(normalized) || epicTitles.has(normalizeTaskTitle(displayEpicTitle(dependsOn)));
}

function TaskCard({ task, epicTitles, variant, buildTask, onOpenChat }: {
  task: AltYBmadTask;
  epicTitles: Set<string>;
  variant?: 'epic';
  buildTask?: AltYBmadBuildTask;
  onOpenChat?: (runId: number) => void;
}) {
  const dependencies = Array.isArray(task.dependsOn)
    ? task.dependsOn.filter((dependency) => !taskDependsOnEpic(dependency, epicTitles))
    : [];
  const title = variant === 'epic' ? displayEpicTitle(task.title) : task.title;
  const status = buildTask?.status || task.status;
  const confidence = typeof buildTask?.confidence === 'number' ? Math.round(buildTask.confidence) : null;

  return (
    <li className={variant === 'epic' ? 'alty-bmad-task epic' : 'alty-bmad-task'}>
      <div className="alty-bmad-task-head">
        {variant === 'epic' ? <span className="alty-bmad-pill epic">Epic</span> : null}
        <span className="alty-bmad-task-title">{title}</span>
        {task.id != null ? <span className="alty-bmad-task-id">#{String(task.id)}</span> : null}
        {status ? <span className={`alty-bmad-task-status ${String(status).replace('_', '-')}`}>{buildStatusLabel(String(status))}</span> : null}
        {confidence != null ? <span className="alty-bmad-task-confidence">{confidence}% proof</span> : null}
        {buildTask?.childRunId && onOpenChat ? (
          <button
            type="button"
            className="alty-bmad-task-open"
            onClick={() => onOpenChat(buildTask.childRunId!)}
            title="Open build subchat"
          >
            <i className="fas fa-code-branch" aria-hidden="true" /> #{buildTask.childRunId}
          </button>
        ) : null}
      </div>
      {task.description ? <p className="alty-bmad-text">{task.description}</p> : null}
      {buildTask?.error ? (
        <p className="alty-bmad-task-error">
          <i className="fas fa-exclamation-triangle" aria-hidden="true" /> {buildTask.error}
        </p>
      ) : null}
      {buildTask?.managerNote ? (
        <p className="alty-bmad-task-manager">
          <i className="fas fa-heartbeat" aria-hidden="true" /> {buildTask.managerNote}
        </p>
      ) : null}
      {dependencies.length ? (
        <p className="alty-bmad-deps">
          <i className="fas fa-link" aria-hidden="true" /> depends on: {dependencies.join(', ')}
        </p>
      ) : null}
    </li>
  );
}

function TasksPanel({ workflow, buildState, onOpenChat }: {
  workflow: AltYBmadWorkflow;
  buildState?: AltYBmadBuildState | null;
  onOpenChat?: (runId: number) => void;
}) {
  const project = workflow.project;
  const tasks: AltYBmadTask[] = Array.isArray(workflow.tasks) ? workflow.tasks : [];
  const epicTasks = tasks.filter(isEpicTask);
  const storyTasks = tasks.filter((task) => !isEpicTask(task));
  const buildByTitle = new Map(
    (buildState?.tasks || []).map((task) => [normalizeTaskTitle(task.title), task]),
  );
  const epicTitles = new Set(
    epicTasks.flatMap((task) => [
      normalizeTaskTitle(task.title),
      normalizeTaskTitle(displayEpicTitle(task.title)),
    ]),
  );
  const confirmed = workflow.status === 'confirmed';
  return (
    <div className="alty-bmad-tasks">
      {buildState ? (
        <div className="alty-bmad-build-summary">
          <span className={`alty-bmad-task-status ${buildState.status.replace('_', '-')}`}>
            {buildStatusLabel(buildState.status)}
          </span>
          <span>{buildState.baseBranch || 'ai'} branch</span>
          <span>{buildState.maxParallel || 1} parallel</span>
          {buildState.provider ? <span>{buildState.provider}</span> : null}
          {buildState.manager?.lastNote ? (
            <span className="alty-bmad-manager-note">
              <i className="fas fa-heartbeat" aria-hidden="true" /> {buildState.manager.lastNote}
            </span>
          ) : null}
        </div>
      ) : null}
      {project ? (
        <div className="alty-bmad-project">
          <span className="alty-bmad-pill existing">
            {project.id != null ? 'Existing project' : 'Project context'}
          </span>
          <span className="alty-bmad-project-title">{project.title || 'Untitled project'}</span>
          {project.id != null ? <span className="alty-bmad-project-id">#{String(project.id)}</span> : null}
        </div>
      ) : null}
      {tasks.length === 0 ? (
        <p className="alty-bmad-text muted">
          {confirmed ? 'No tasks were created.' : 'Tasks appear here once the workflow includes them.'}
        </p>
      ) : (
        <>
          {epicTasks.length ? (
            <ul className="alty-bmad-task-list">
              {epicTasks.map((task, i) => (
                <TaskCard key={`epic-${i}`} task={task} epicTitles={epicTitles} variant="epic" />
              ))}
            </ul>
          ) : null}
          {storyTasks.length ? (
            <div className={epicTasks.length ? 'alty-bmad-story-group' : undefined}>
              {epicTasks.length ? <div className="alty-bmad-story-label">Stories</div> : null}
              <ul className="alty-bmad-task-list">
                {storyTasks.map((task, i) => (
                  <TaskCard
                    key={`story-${i}`}
                    task={task}
                    epicTitles={epicTitles}
                    buildTask={buildByTitle.get(normalizeTaskTitle(task.title))}
                    onOpenChat={onOpenChat}
                  />
                ))}
              </ul>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

// The right-pane (desktop) / full-screen overlay (mobile) view the Customized
// BMad Agent Flow's clipboard toggle flips to. Renders the living workflow the
// agent emits — user story first, then rephrase + sources + BMad pieces, then the dependent
// build tasks — and a Build button that launches managed implementation
// subchats. The pieces are edited by chatting with the agent.
export function BmadWorkflowView({ workflow, buildState, onBuild, onOpenChat, onClose, busy }: BmadWorkflowViewProps) {
  const gaps = asLines(workflow.gaps);
  const sources = normalizeSources(workflow.sources);
  const questions = normalizeQuestions(workflow.questions);
  const userStory = asLines(workflow.userStory);
  const architectureDecisions = normalizeArchitectureDecisions(workflow.architectureDecisions);
  const hasDesign = !!workflow.design && (
    !!workflow.design.summary
    || (Array.isArray(workflow.design.pages) && workflow.design.pages.length > 0)
    || !!workflow.design.notes
  );
  const hasTasks = Array.isArray(workflow.tasks) && workflow.tasks.length > 0;
  const confirmed = workflow.status === 'confirmed';

  const tabs = useMemo<Tab[]>(() => [
    {
      id: 'story', label: 'User story', icon: 'fa-list-ul', available: userStory.length > 0,
      render: () => <StoryPanel items={userStory} designPages={workflow.design?.pages} />,
    },
    {
      id: 'rephrase', label: 'Rephrase', icon: 'fa-align-left', available: !!workflow.rephrase,
      render: () => <p className="alty-bmad-text lead">{workflow.rephrase}</p>,
    },
    {
      id: 'sources', label: 'Sources', icon: 'fa-book-open', available: !!workflow.rephrase || sources.length > 0,
      render: () => <SourcesPanel sources={sources} />,
    },
    {
      id: 'gaps', label: 'Gaps', icon: 'fa-puzzle-piece', available: gaps.length > 0,
      render: () => <ListPanel items={gaps} />,
    },
    {
      id: 'questions', label: 'Questions', icon: 'fa-question-circle', available: questions.length > 0,
      render: () => <QuestionsPanel questions={questions} />,
    },
    {
      id: 'design', label: 'Design', icon: 'fa-pen-nib', available: hasDesign,
      render: () => <DesignPanel design={workflow.design!} storyItems={userStory} />,
    },
    {
      id: 'architecture', label: 'Architecture', icon: 'fa-sitemap', available: architectureDecisions.length > 0,
      render: () => <ArchitecturePanel decisions={architectureDecisions} />,
    },
    {
      id: 'tasks', label: 'Dev tasks', icon: 'fa-tasks', available: hasTasks,
      render: () => <TasksPanel workflow={workflow} buildState={buildState} onOpenChat={onOpenChat} />,
    },
  ], [workflow, gaps, sources, questions, userStory, architectureDecisions, hasDesign, hasTasks, buildState, onOpenChat]);

  const availableTabs = tabs.filter((t) => t.available);
  const [activeId, setActiveId] = useState<string>(availableTabs[0]?.id ?? 'story');
  const userSelectedTabRef = useRef(false);
  // Clamp the active tab to one that still exists (content evolves each turn).
  // Until the user picks a tab manually, follow the first available tab so User
  // story becomes the default as soon as the agent emits it.
  useEffect(() => {
    if (!availableTabs.length) return;
    const preferredId = availableTabs[0].id;
    if (!availableTabs.some((t) => t.id === activeId)) {
      userSelectedTabRef.current = false;
      setActiveId(preferredId);
    } else if (!userSelectedTabRef.current && activeId !== preferredId) {
      setActiveId(preferredId);
    }
  }, [availableTabs, activeId]);
  const active = availableTabs.find((t) => t.id === activeId) ?? availableTabs[0] ?? null;
  const buildStarted = !!buildState;
  const buildComplete = buildState?.status === 'complete';
  const buildNeedsReview = buildState?.status === 'needs_review';

  return (
    <div className="alty-bmad-view">
      <header className="alty-bmad-head">
        <span className="alty-bmad-head-title">
          <i className="fas fa-clipboard-list" aria-hidden="true" />
          <span>BMad workflow</span>
          {workflow.kind === 'bug' ? <span className="alty-bmad-kind">bug</span> : null}
        </span>
        {onClose ? (
          <button type="button" className="alty-icon-button" title="Back" aria-label="Back" onClick={onClose}>
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        ) : null}
      </header>

      {availableTabs.length === 0 ? (
        <div className="alty-bmad-empty">
          <i className="fas fa-clipboard-list" aria-hidden="true" />
          <p>Working on it…</p>
          <p className="alty-bmad-empty-hint">Describe your goal and the rephrase, sources, gaps, questions, user story, design and architecture will appear here.</p>
        </div>
      ) : (
        <>
          <nav className="alty-bmad-tabs" role="tablist">
            {availableTabs.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={t.id === active?.id}
                className={t.id === active?.id ? 'alty-bmad-tab active' : 'alty-bmad-tab'}
                onClick={() => {
                  userSelectedTabRef.current = true;
                  setActiveId(t.id);
                }}
              >
                <i className={`fas ${t.icon}`} aria-hidden="true" />
                <span>{t.label}</span>
              </button>
            ))}
          </nav>
          <div className="alty-bmad-body" role="tabpanel">
            {active ? active.render() : null}
          </div>
        </>
      )}

      <footer className="alty-bmad-foot">
        {buildComplete ? (
          <span className="alty-bmad-confirmed">
            <i className="fas fa-check-double" aria-hidden="true" /> Build complete on ai
          </span>
        ) : buildNeedsReview ? (
          <span className="alty-bmad-review">
            <i className="fas fa-triangle-exclamation" aria-hidden="true" /> Build needs review
          </span>
        ) : buildStarted ? (
          <span className="alty-bmad-confirmed">
            <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Build running
          </span>
        ) : confirmed ? (
          <span className="alty-bmad-confirmed">
            <i className="fas fa-check-double" aria-hidden="true" /> Development tasks created
          </span>
        ) : onBuild && workflow.rephrase ? (
          <button
            type="button"
            className="alty-bmad-confirm"
            onClick={onBuild}
            disabled={!!busy || !hasTasks}
            title="Launch dependency-aware dev subchats"
          >
            <i className={`fas ${busy ? 'fa-spinner fa-spin' : 'fa-hammer'}`} aria-hidden="true" />
            <span>{busy ? 'Working…' : 'Build'}</span>
          </button>
        ) : null}
      </footer>
    </div>
  );
}
